import { Table, Tbody, Tr, Td, type TableProps, Flex } from "@chakra-ui/react";
import { memo, type FC, useMemo } from "react";
import { type Column, useTable } from "react-table";
import { Typography } from "@/components/essentials/typography";
import { css } from "@emotion/react";
import { Image } from "@chakra-ui/image";
import { Box } from "@/components/essentials/box";
import { CopyClipboardButton } from "@/components/fragments/button/copy-clipboard-button/CopyClipboardButton";

export interface KeyValue {
  readonly key: string;
  readonly value: string | React.ReactNode;
  readonly isImagePathKey?: boolean;
  readonly isStrongValue?: boolean;
  readonly isAttentionValue?: boolean;
  readonly isCopyableValue?: boolean;
}
export interface KeyValueTableProps extends Omit<TableProps, "colorScheme" | "size"> {
  readonly data: KeyValue[];
  readonly "data-testid"?: string;
}

export const KeyValueTable: FC<KeyValueTableProps> = memo(({ data, "data-testid": dataTestid }) => {
  // biome-ignore lint/style/useConsistentArrayType :[AUTOMATICALLY-INSERTED] Use shorthand T[] syntax instead of Array<T> syntax.
  const columns: Array<Column<KeyValue>> = useMemo(
    () => [
      {
        Header: "key",
        accessor: "key",
        Cell: (props) => {
          return props.row.original.isImagePathKey ? (
            <Image src={props.value} alt="" w="100px" margin="auto" />
          ) : (
            <>{props.value}</>
          );
        },
      },
      {
        Header: "value",
        accessor: "value",
        Cell: (props) => {
          return props.row.original.isCopyableValue ? (
            <Flex justifyContent="space-between">
              <Box
                fontWeight={props.row.original.isStrongValue ? "bold" : "normal"}
                color={props.row.original.isAttentionValue ? "ui.red.main" : "normal"}
                wordBreak="break-word"
              >
                {props.value}
              </Box>
              <Box>
                <CopyClipboardButton copyValue={typeof props.value === "string" ? props.value : ""} size="sm" />
              </Box>
            </Flex>
          ) : (
            <Box
              as="span"
              fontWeight={props.row.original.isStrongValue ? "bold" : "normal"}
              color={props.row.original.isAttentionValue ? "ui.red.main" : "normal"}
              wordBreak="break-word"
            >
              {props.value}
            </Box>
          );
        },
      },
    ],
    [],
  );
  const { getTableProps, getTableBodyProps, rows, prepareRow } = useTable<KeyValue>({
    columns,
    data,
  });

  return (
    <Table
      borderRadius="1rem"
      border="solid"
      borderWidth="1px"
      borderColor="border.main"
      overflow="hidden"
      css={css(`
       & {
        border-collapse: separate;
        border-spacing: 0;
       }
      `)}
      data-testid={dataTestid}
      {...getTableProps}
    >
      <Tbody {...getTableBodyProps()}>
        {rows.map((row, rowKey, rowArray) => {
          prepareRow(row);
          const { key, ...rowProps } = row.getRowProps();
          return (
            <Tr key={key} {...rowProps}>
              {row.cells.map((cell) => {
                const { key, ...cellProps } = cell.getCellProps();

                return (
                  <Td
                    key={key}
                    pl="0.5rem"
                    pr={cell.column.Header === "value" ? "0.5rem" : "0rem"}
                    py="1rem"
                    borderBottomWidth={rowArray.length - 1 === rowKey ? "0px" : "1px"}
                    borderColor="border.main"
                    bgColor={cell.column.Header === "value" ? "background.main" : "light.main"}
                    w={cell.column.Header === "value" ? "70%" : "30%"}
                    {...cellProps}
                  >
                    <Typography
                      variant={{ base: "text4", sm: "text3", md: "text2", lg: "text2", xl: "text2", "2xl": "text2" }}
                    >
                      {cell.render("Cell")}
                    </Typography>
                  </Td>
                );
              })}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
});

KeyValueTable.displayName = "KeyValueTable";
